import React from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  FileText,
  TrendingUp,
  BarChart3,
  PieChart,
  Clock,
  Target,
  Activity,
  Calendar
} from 'lucide-react';

const Reports: React.FC = () => {
  return (
    <Layout currentPage="reports">
      <div className="h-full overflow-y-auto custom-scrollbar">
        <div className="p-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground-primary mb-2 neon-blue">
              Relatórios
            </h1>
            <p className="text-foreground-tertiary">
              Acompanhe métricas e indicadores dos seus projetos
            </p>
          </div>

          {/* Under Development Notice */}
          <Card variant="glass" className="w-full mb-8 border-2 border-blue-500/30">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center">
                <Activity className="w-10 h-10 text-blue-400 animate-pulse" />
              </div>
              <h2 className="text-2xl font-bold text-foreground-primary mb-3">
                Em Desenvolvimento
              </h2>
              <p className="text-foreground-secondary text-lg mb-2">
                A funcionalidade de Relatórios está sendo desenvolvida e estará disponível em breve.
              </p>
              <p className="text-foreground-tertiary">
                Estamos trabalhando para trazer a melhor experiência em visualização de dados e métricas.
              </p>
            </CardContent>
          </Card>

          {/* Preview Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Time Reports Card */}
            <Card variant="glass" className="cursor-not-allowed opacity-60" aria-disabled="true" role="article">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5 text-blue-400" />
                  Relatório de Horas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Visualize o tempo investido por projeto, área e período.
                </p>
              </CardContent>
            </Card>

            {/* Progress Reports Card */}
            <Card variant="glass" className="cursor-not-allowed opacity-60" aria-disabled="true" role="article">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  Progresso de Projetos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Acompanhe o andamento e conclusão de projetos e tarefas.
                </p>
              </CardContent>
            </Card>

            {/* Performance Metrics Card */}
            <Card variant="glass" className="cursor-not-allowed opacity-60" aria-disabled="true" role="article">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                  Métricas de Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Análise de produtividade e eficiência da equipe.
                </p>
              </CardContent>
            </Card>

            {/* Distribution Reports Card */}
            <Card variant="glass" className="cursor-not-allowed opacity-60" aria-disabled="true" role="article">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <PieChart className="w-5 h-5 text-orange-400" />
                  Distribuição de Tarefas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Visualize como as tarefas estão distribuídas por área.
                </p>
              </CardContent>
            </Card>

            {/* Deadlines Card */}
            <Card variant="glass" className="cursor-not-allowed opacity-60" aria-disabled="true" role="article">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Calendar className="w-5 h-5 text-red-400" />
                  Prazos e Entregas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Acompanhe prazos e cumprimento de cronogramas.
                </p>
              </CardContent>
            </Card>

            {/* Goals Card */}
            <Card variant="glass" className="cursor-not-allowed opacity-60" aria-disabled="true" role="article">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Target className="w-5 h-5 text-cyan-400" />
                  Metas e Objetivos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Monitoramento de metas e alcance de objetivos.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Additional Info */}
          <Card variant="glass" className="w-full mt-8">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <FileText className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-foreground-primary mb-2">
                    O que esperar dos Relatórios?
                  </h3>
                  <ul className="space-y-2 text-foreground-secondary">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>Dashboards interativos com métricas em tempo real</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>Filtros personalizáveis por período, projeto e equipe</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>Exportação de relatórios em PDF e Excel</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>Comparativos entre períodos e projetos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>Gráficos e visualizações intuitivas</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Reports;
